import { Link } from "react-router-dom";
import { ArrowLeft, Target } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800/50">
        <div className="container mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Target className="h-6 w-6 text-emerald-400" />
            <span className="font-bold text-lg">IntentDrive</span>
            <span className="text-xs text-emerald-400">.ai</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-16 max-w-4xl">
        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-slate-400 mb-12">
          Effective Date: April 1, 2026
        </p>

        <div className="space-y-10 text-slate-300 leading-relaxed">
          {/* Intro */}
          <section>
            <p>
              IntentDrive.ai ("we," "us," or "our") is committed to protecting
              your privacy. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you use our
              platform at{" "}
              <a
                href="https://intentdrive.ai"
                className="text-emerald-400 hover:underline"
              >
                intentdrive.ai
              </a>{" "}
              (the "Service"). Please read this policy carefully. By using the
              Service, you consent to the data practices described in this
              policy.
            </p>
          </section>

          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              1. Information We Collect
            </h2>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">
              1.1 Account Information
            </h3>
            <p>
              When you create an account, we collect your name, email address,
              password (stored in hashed form), company name, and billing
              information (processed by our payment provider).
            </p>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">
              1.2 Chatbot Configurations
            </h3>
            <p>
              We store the chatbot flows, conversation scripts, widget
              settings, and other configurations you create using our builder
              tools.
            </p>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">
              1.3 Conversation Data
            </h3>
            <p>
              We collect and store the conversations that occur between your
              chatbot widgets and your end-users, including messages, timestamps,
              and session metadata.
            </p>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">
              1.4 Lead Data
            </h3>
            <p>
              When end-users interact with your chatbots, we may collect
              information they provide such as names, email addresses, phone
              numbers, and any other data fields configured in your chatbot
              flows. This data is stored on your behalf and routed via webhooks
              as configured by you.
            </p>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">
              1.5 Usage and Analytics Data
            </h3>
            <p>
              We automatically collect information about how you and your
              end-users interact with the Service, including page views,
              chatbot engagement metrics, conversion rates, device information,
              browser type, IP addresses, and referring URLs.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              2. How We Use Your Information
            </h2>
            <p className="mb-3">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                <span className="text-white font-medium">Provide the Service:</span>{" "}
                To operate, maintain, and deliver the features and functionality
                of the platform, including hosting chatbot widgets, processing
                conversations, and routing lead data.
              </li>
              <li>
                <span className="text-white font-medium">Improve the Product:</span>{" "}
                To understand how users interact with our platform, identify
                issues, and develop new features and improvements.
              </li>
              <li>
                <span className="text-white font-medium">Analytics:</span>{" "}
                To generate aggregated, anonymized analytics and reporting for
                both our internal use and to provide you with performance
                insights.
              </li>
              <li>
                <span className="text-white font-medium">Communications:</span>{" "}
                To send you account-related notifications, service updates,
                security alerts, and marketing communications (which you can
                opt out of at any time).
              </li>
              <li>
                <span className="text-white font-medium">Billing:</span>{" "}
                To process payments and manage your subscription.
              </li>
              <li>
                <span className="text-white font-medium">Legal Compliance:</span>{" "}
                To comply with legal obligations, resolve disputes, and enforce
                our agreements.
              </li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              3. Data Sharing and Disclosure
            </h2>
            <p className="mb-3">
              <span className="text-white font-medium">
                We do not sell your personal data.
              </span>{" "}
              We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                <span className="text-white font-medium">Service Providers:</span>{" "}
                We share data with trusted third-party service providers who
                assist us in operating the platform, including Supabase
                (database and authentication), Stripe (payment processing), and
                AI model providers (to power chatbot conversations). These
                providers are contractually obligated to protect your data.
              </li>
              <li>
                <span className="text-white font-medium">Webhooks and Integrations:</span>{" "}
                Lead data collected through your chatbots is transmitted to
                the webhook endpoints and integrations you configure. You are
                responsible for the privacy practices of those destinations.
              </li>
              <li>
                <span className="text-white font-medium">Legal Requirements:</span>{" "}
                We may disclose your information if required to do so by law or
                in response to valid requests by public authorities (e.g., a
                court or government agency).
              </li>
              <li>
                <span className="text-white font-medium">Business Transfers:</span>{" "}
                In the event of a merger, acquisition, or sale of assets, your
                data may be transferred as part of that transaction. We will
                notify you of any such change.
              </li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              4. Cookies and Tracking Technologies
            </h2>
            <p className="mb-3">
              We use cookies and similar tracking technologies to collect and
              track information about your activity on our Service. These
              include:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                <span className="text-white font-medium">Essential Cookies:</span>{" "}
                Required for authentication, session management, and core
                platform functionality.
              </li>
              <li>
                <span className="text-white font-medium">Analytics Cookies:</span>{" "}
                Used to understand how visitors interact with our website and
                platform to improve the user experience.
              </li>
              <li>
                <span className="text-white font-medium">Chatbot Widget Tracking:</span>{" "}
                Our embedded chat widgets may use local storage and session
                identifiers to maintain conversation state across page
                navigations.
              </li>
            </ul>
            <p className="mt-3">
              You can control cookie preferences through your browser settings.
              Note that disabling certain cookies may affect the functionality of
              the Service.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              5. Data Retention
            </h2>
            <p className="mb-3">
              We retain your information for as long as your account is active
              or as needed to provide you with the Service. Specific retention
              periods include:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                <span className="text-white font-medium">Account Data:</span>{" "}
                Retained for the duration of your account and up to 30 days
                after account deletion.
              </li>
              <li>
                <span className="text-white font-medium">Conversation Data:</span>{" "}
                Retained according to your plan settings. You may delete
                conversation data at any time through the platform.
              </li>
              <li>
                <span className="text-white font-medium">Lead Data:</span>{" "}
                Retained until you delete it or your account is terminated.
              </li>
              <li>
                <span className="text-white font-medium">Analytics Data:</span>{" "}
                Aggregated analytics data may be retained indefinitely in
                anonymized form.
              </li>
            </ul>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              6. Your Rights
            </h2>
            <p className="mb-3">
              Depending on your location, you may have the following rights
              regarding your personal data:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                <span className="text-white font-medium">Access:</span>{" "}
                Request a copy of the personal data we hold about you.
              </li>
              <li>
                <span className="text-white font-medium">Correction:</span>{" "}
                Request that we correct inaccurate or incomplete data.
              </li>
              <li>
                <span className="text-white font-medium">Deletion:</span>{" "}
                Request that we delete your personal data, subject to certain
                legal exceptions.
              </li>
              <li>
                <span className="text-white font-medium">Export:</span>{" "}
                Request a portable copy of your data in a machine-readable
                format.
              </li>
              <li>
                <span className="text-white font-medium">Opt-Out:</span>{" "}
                Opt out of marketing communications at any time by using the
                unsubscribe link in our emails or contacting us directly.
              </li>
              <li>
                <span className="text-white font-medium">Restriction:</span>{" "}
                Request that we restrict the processing of your data under
                certain circumstances.
              </li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact us at{" "}
              <a
                href="mailto:support@intentdrive.ai"
                className="text-emerald-400 hover:underline"
              >
                support@intentdrive.ai
              </a>
              . We will respond to your request within 30 days.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              7. CCPA Compliance (California Residents)
            </h2>
            <p className="mb-3">
              If you are a California resident, the California Consumer Privacy
              Act (CCPA) provides you with additional rights regarding your
              personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                The right to know what personal information is collected, used,
                shared, or sold.
              </li>
              <li>The right to delete personal information held by us.</li>
              <li>The right to opt out of the sale of personal information.</li>
              <li>
                The right to non-discrimination for exercising your CCPA rights.
              </li>
            </ul>
            <p className="mt-3">
              As stated above, we do not sell personal information. To submit a
              CCPA request, contact us at{" "}
              <a
                href="mailto:support@intentdrive.ai"
                className="text-emerald-400 hover:underline"
              >
                support@intentdrive.ai
              </a>
              .
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              8. GDPR Compliance (European Economic Area Residents)
            </h2>
            <p className="mb-3">
              If you are located in the European Economic Area (EEA), we
              process your personal data under the following legal bases:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                <span className="text-white font-medium">Contract:</span>{" "}
                Processing necessary to perform our contract with you (providing
                the Service).
              </li>
              <li>
                <span className="text-white font-medium">Legitimate Interest:</span>{" "}
                Processing necessary for our legitimate business interests, such
                as improving the Service and preventing fraud.
              </li>
              <li>
                <span className="text-white font-medium">Consent:</span>{" "}
                Processing based on your consent, such as marketing
                communications.
              </li>
              <li>
                <span className="text-white font-medium">Legal Obligation:</span>{" "}
                Processing necessary to comply with applicable laws.
              </li>
            </ul>
            <p className="mt-3">
              You have the right to lodge a complaint with your local data
              protection authority if you believe your rights have been violated.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              9. Data Security
            </h2>
            <p>
              We implement industry-standard security measures to protect your
              data, including encryption in transit (TLS/SSL), encryption at
              rest, access controls, and regular security assessments. However,
              no method of transmission over the internet or electronic storage
              is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              10. Children's Privacy
            </h2>
            <p>
              The Service is not directed to individuals under the age of 18. We
              do not knowingly collect personal information from children. If we
              become aware that we have collected personal information from a
              child, we will take steps to delete that information promptly.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              11. Third-Party Links
            </h2>
            <p>
              The Service may contain links to third-party websites or services
              that are not owned or controlled by IntentDrive.ai. We are not
              responsible for the privacy practices of these third parties. We
              encourage you to review the privacy policies of any third-party
              sites you visit.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              12. Changes to This Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy on
              this page and updating the "Effective Date" above. Material
              changes will be communicated via email or a prominent notice on
              the Service. Your continued use of the Service after any changes
              constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              13. Contact Us
            </h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or
              our data practices, please contact us at:
            </p>
            <div className="mt-3 p-4 rounded-lg bg-slate-900 border border-slate-800">
              <p className="text-white font-medium">IntentDrive.ai</p>
              <p>
                Email:{" "}
                <a
                  href="mailto:support@intentdrive.ai"
                  className="text-emerald-400 hover:underline"
                >
                  support@intentdrive.ai
                </a>
              </p>
              <p>
                Website:{" "}
                <a
                  href="https://intentdrive.ai"
                  className="text-emerald-400 hover:underline"
                >
                  intentdrive.ai
                </a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 py-8">
        <div className="container mx-auto px-6 text-center text-sm text-slate-600">
          &copy; 2026 IntentDrive.ai. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
